import React, { useState, useRef, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from '@/hooks/use-toast';
import {
  Play,
  Camera,
  Video,
  Loader2,
  CheckCircle,
  Download
} from 'lucide-react';
import { videoAnalysisSystem, VideoAnalysisResult, Pose } from '../utils/videoAnalysis';
import { cn } from '@/lib/utils';

interface VideoAnalysisProps {
  className?: string;
  onAnalysisComplete?: (result: VideoAnalysisResult) => void;
}

export const VideoAnalysis: React.FC<VideoAnalysisProps> = ({
  className,
  onAnalysisComplete
}) => {
  const [isInitializing, setIsInitializing] = useState(false);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [videoFile, setVideoFile] = useState<File | null>(null);
  const [videoUrl, setVideoUrl] = useState<string | null>(null);
  const [analysisResult, setAnalysisResult] = useState<VideoAnalysisResult | null>(null);
  const [currentPose, setCurrentPose] = useState<Pose | null>(null);
  const [progress, setProgress] = useState(0);
  const [, setIsPlaying] = useState(false);
  const [mode, setMode] = useState<'upload' | 'webcam'>('upload');
  
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const webcamRef = useRef<HTMLVideoElement>(null);

  // Initialize the video analysis system
  useEffect(() => {
    const init = async () => {
      setIsInitializing(true);
      try {
        await videoAnalysisSystem.initialize();
        toast({
          title: 'System Ready',
          description: 'Video analysis system initialized successfully',
        });
      } catch (error) {
        console.error('Initialization error:', error);
        toast({
          title: 'Limited Mode',
          description: 'Video analysis running in demo mode. Some features may be limited.',
        });
      } finally {
        setIsInitializing(false);
      }
    };
    
    init();
    
    return () => {
      videoAnalysisSystem.dispose();
    };
  }, []);

  // Handle file upload
  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file && file.type.startsWith('video/')) {
      setVideoFile(file);
      const url = URL.createObjectURL(file);
      setVideoUrl(url);
      setAnalysisResult(null);
      setProgress(0);
    } else {
      toast({
        title: 'Invalid File',
        description: 'Please upload a video file',
        variant: 'destructive'
      });
    }
  };

  // Start video analysis
  const startAnalysis = async () => {
    if (!videoRef.current || !videoUrl) return;
    
    setIsAnalyzing(true);
    setProgress(0);
    
    try {
      const result = await videoAnalysisSystem.analyzeVideo(
        videoRef.current,
        (prog) => setProgress(prog)
      );
      
      setAnalysisResult(result);
      
      if (onAnalysisComplete) {
        onAnalysisComplete(result);
      }
      
      toast({
        title: 'Analysis Complete',
        description: `Shot classified as ${result.shotClassification.type} with ${(result.shotClassification.confidence * 100).toFixed(0)}% confidence`,
      });
    } catch (error) {
      console.error('Analysis error:', error);
      toast({
        title: 'Analysis Failed',
        description: 'Failed to analyze video. Please try again.',
        variant: 'destructive'
      });
    } finally {
      setIsAnalyzing(false);
      setProgress(100);
    }
  };

  // Analyze current frame
  const analyzeCurrentFrame = async () => {
    if (!videoRef.current) return;
    
    try {
      const pose = await videoAnalysisSystem.analyzeFrame(videoRef.current);
      setCurrentPose(pose);
      
      // Draw pose on canvas
      if (pose && canvasRef.current) {
        drawPoseOnCanvas(pose);
      }
    } catch (error) {
      console.error('Frame analysis error:', error);
    }
  };

  // Draw pose keypoints on canvas
  const drawPoseOnCanvas = (pose: Pose) => {
    if (!canvasRef.current || !videoRef.current) return;
    
    const ctx = canvasRef.current.getContext('2d');
    if (!ctx) return;
    
    // Set canvas size to match video
    canvasRef.current.width = videoRef.current.videoWidth;
    canvasRef.current.height = videoRef.current.videoHeight;
    
    // Clear canvas
    ctx.clearRect(0, 0, canvasRef.current.width, canvasRef.current.height);
    
    // Draw keypoints
    pose.keypoints.forEach(keypoint => {
      if (keypoint.score && keypoint.score > 0.3) {
        ctx.beginPath();
        ctx.arc(keypoint.x, keypoint.y, 5, 0, 2 * Math.PI);
        ctx.fillStyle = keypoint.score > 0.7 ? '#10b981' : '#f59e0b';
        ctx.fill();
      }
    });
    
    // Draw skeleton connections
    const connections = [
      [5, 6], [5, 7], [7, 9], [6, 8], [8, 10], // Arms
      [5, 11], [6, 12], [11, 12], // Torso
      [11, 13], [13, 15], [12, 14], [14, 16] // Legs
    ];
    
    ctx.strokeStyle = '#3b82f6';
    ctx.lineWidth = 2;
    
    connections.forEach(([start, end]) => {
      const startPoint = pose.keypoints[start];
      const endPoint = pose.keypoints[end];
      
      if (startPoint.score && startPoint.score > 0.3 &&
          endPoint.score && endPoint.score > 0.3) {
        ctx.beginPath();
        ctx.moveTo(startPoint.x, startPoint.y);
        ctx.lineTo(endPoint.x, endPoint.y);
        ctx.stroke();
      }
    });
  };

  // Start webcam
  const startWebcam = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ 
        video: { 
          width: 1280, 
          height: 720,
          facingMode: 'environment'
        } 
      });
      
      if (webcamRef.current) {
        webcamRef.current.srcObject = stream;
        setMode('webcam');
      }
    } catch (error) {
      console.error('Webcam error:', error);
      toast({
        title: 'Webcam Error',
        description: 'Failed to access webcam',
        variant: 'destructive'
      });
    }
  };

  // Capture webcam frame
  const captureWebcamFrame = async () => {
    if (!webcamRef.current) return;
    
    try {
      const pose = await videoAnalysisSystem.analyzeFrame(webcamRef.current);
      setCurrentPose(pose);
      
      toast({
        title: 'Frame Captured',
        description: pose ? 'Pose detected successfully' : 'No pose detected',
      });
    } catch (error) {
      console.error('Capture error:', error);
    }
  };

  // Download sample video
  const downloadSampleVideo = () => {
    // In production, this would download from your server
    window.open('https://github.com/ascuet/CricShot10', '_blank');
  };

  return (
    <div className={cn('space-y-6', className)}>
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold flex items-center gap-2">
            <Video className="h-6 w-6" />
            Video Analysis
          </h2>
          <p className="text-muted-foreground">
            AI-powered batting technique analysis from video
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={downloadSampleVideo}
          >
            <Download className="h-4 w-4 mr-2" />
            Sample Videos
          </Button>
          <Button
            variant={mode === 'webcam' ? 'default' : 'outline'}
            size="sm"
            onClick={startWebcam}
            disabled={isInitializing}
          >
            <Camera className="h-4 w-4 mr-2" />
            Use Webcam
          </Button>
        </div>
      </div>

      {/* Initialization Status */}
      {isInitializing && (
        <Alert>
          <Loader2 className="h-4 w-4 animate-spin" />
          <AlertDescription>
            Initializing video analysis system...
          </AlertDescription>
        </Alert>
      )}

      <Tabs value={mode} onValueChange={(v: string) => setMode(v as 'upload' | 'webcam')} className="space-y-4">
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="upload">Upload Video</TabsTrigger>
          <TabsTrigger value="webcam">Live Webcam</TabsTrigger>
        </TabsList>

        {/* Upload Video Tab */}
        <TabsContent value="upload" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Video Upload</CardTitle>
              <CardDescription>
                Upload a cricket batting video for analysis
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* File Upload */}
              <div>
                <Label htmlFor="video-upload">Select Video File</Label>
                <Input
                  id="video-upload"
                  ref={fileInputRef}
                  type="file"
                  accept="video/*"
                  onChange={handleFileUpload}
                  className="mt-2"
                />
              </div>

              {/* Video Player */}
              {videoUrl && (
                <div className="relative">
                  <video
                    ref={videoRef}
                    src={videoUrl}
                    controls
                    className="w-full rounded-lg"
                    onPlay={() => setIsPlaying(true)}
                    onPause={() => setIsPlaying(false)}
                    onTimeUpdate={analyzeCurrentFrame}
                  />
                  <canvas
                    ref={canvasRef}
                    className="absolute top-0 left-0 w-full h-full pointer-events-none"
                  />
                </div>
              )}

              {/* Analysis Controls */}
              {videoFile && (
                <div className="space-y-4">
                  <Button
                    onClick={startAnalysis}
                    disabled={isAnalyzing || isInitializing}
                    className="w-full"
                  >
                    {isAnalyzing ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Analyzing... {progress.toFixed(0)}%
                      </>
                    ) : (
                      <>
                        <Play className="mr-2 h-4 w-4" />
                        Start Analysis
                      </>
                    )}
                  </Button>

                  {isAnalyzing && (
                    <Progress value={progress} className="w-full" />
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Webcam Tab */}
        <TabsContent value="webcam" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Live Webcam Analysis</CardTitle>
              <CardDescription>
                Analyze batting technique in real-time
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="relative aspect-video bg-black rounded-lg overflow-hidden">
                <video
                  ref={webcamRef}
                  autoPlay
                  playsInline
                  muted
                  className="w-full h-full"
                />
              </div>
              
              <Button
                onClick={captureWebcamFrame}
                disabled={isInitializing}
                className="w-full"
              >
                <Camera className="mr-2 h-4 w-4" />
                Capture & Analyze Frame
              </Button>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Analysis Results */}
      {analysisResult && (
        <Card>
          <CardHeader>
            <CardTitle>Analysis Results</CardTitle>
            <CardDescription>
              Technique analysis and recommendations
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Shot Classification */}
            <div className="flex items-center justify-between p-4 bg-muted rounded-lg">
              <div>
                <p className="text-sm text-muted-foreground">Shot Type</p>
                <p className="text-2xl font-bold capitalize">
                  {analysisResult.shotClassification.type.replace('_', ' ')}
                </p>
              </div>
              <div className="text-right">
                <p className="text-sm text-muted-foreground">Confidence</p>
                <p className="text-2xl font-bold">
                  {(analysisResult.shotClassification.confidence * 100).toFixed(0)}%
                </p>
              </div>
            </div>

            {/* Technique Metrics */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <div className="flex justify-between">
                  <span className="text-sm">Stance</span>
                  <Badge variant={analysisResult.technique.stance === 'good' ? 'default' : 'destructive'}>
                    {analysisResult.technique.stance}
                  </Badge>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm">Back Lift</span>
                  <span className="font-mono">{analysisResult.technique.backLift.toFixed(0)}°</span>
                </div>
              </div>
              <div className="space-y-2">
                <div className="flex justify-between">
                  <span className="text-sm">Follow Through</span>
                  <span className="font-mono">{analysisResult.technique.followThrough.toFixed(0)}°</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm">Balance</span>
                  <span className="font-mono">{analysisResult.technique.balance.toFixed(0)}%</span>
                </div>
              </div>
            </div>

            {/* Balance Score Visualization */}
            <div>
              <Label>Overall Balance Score</Label>
              <Progress value={analysisResult.technique.balance} className="mt-2" />
            </div>

            {/* Recommendations */}
            <div>
              <h4 className="font-medium mb-3">Coaching Recommendations</h4>
              <div className="space-y-2">
                {analysisResult.recommendations.map((rec, index) => (
                  <Alert key={index}>
                    <CheckCircle className="h-4 w-4" />
                    <AlertDescription>{rec}</AlertDescription>
                  </Alert>
                ))}
              </div>
            </div>

            {/* Frame Analysis Summary */}
            <div className="p-4 bg-muted rounded-lg">
              <p className="text-sm text-muted-foreground mb-2">Analysis Summary</p>
              <div className="grid grid-cols-3 gap-4 text-sm">
                <div>
                  <span className="text-muted-foreground">Frames Analyzed:</span>
                  <span className="ml-2 font-medium">{analysisResult.frames.length}</span>
                </div>
                <div>
                  <span className="text-muted-foreground">Poses Detected:</span>
                  <span className="ml-2 font-medium">
                    {analysisResult.frames.filter(f => f.pose !== null).length}
                  </span>
                </div>
                <div>
                  <span className="text-muted-foreground">Detection Rate:</span>
                  <span className="ml-2 font-medium">
                    {((analysisResult.frames.filter(f => f.pose !== null).length / analysisResult.frames.length) * 100).toFixed(0)}%
                  </span>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Current Pose Info */}
      {currentPose && (
        <Card>
          <CardHeader>
            <CardTitle>Current Frame Pose</CardTitle>
            <CardDescription>
              Real-time pose detection
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-muted-foreground">Keypoints Detected</p>
                <p className="text-xl font-bold">{currentPose.keypoints.length}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Confidence</p>
                <p className="text-xl font-bold">
                  {currentPose.score ? (currentPose.score * 100).toFixed(0) : 'N/A'}%
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};